import React, { useState, useEffect } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  GoogleAuthProvider,
} from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { useNavigate } from 'react-router-dom';

const WORKER_AGREEMENTS = [
  { id: 'independent_contractor', text: 'I understand I am an independent contractor, not an employee of TradePower', required: true },
  { id: 'marketplace_only', text: 'I understand TradePower is a technology marketplace only and does not direct or control my work', required: true },
  { id: 'taxes', text: 'I am responsible for my own taxes and will receive a 1099-NEC if I earn over $600 annually', required: true },
  { id: 'tools', text: 'I am responsible for my own tools, equipment, and transportation', required: true },
  { id: 'work_authorized', text: 'I am authorized to work in the United States', required: true },
  { id: 'liability', text: 'I carry my own liability for work performed on job sites', required: true },
];

const CONTRACTOR_AGREEMENTS = [
  { id: 'not_employer', text: 'I understand TradePower is a technology marketplace and not an employer of any worker', required: true },
  { id: 'supervising', text: 'I am solely responsible for supervising and directing any worker I engage through the TradePower marketplace', required: true },
  { id: 'liability', text: 'I assume all liability for work performed on my job sites', required: true },
  { id: 'insurance', text: 'I confirm my insurance covers workers engaged through TradePower', required: false },
  { id: 'no_tradepower_liability', text: 'I understand TradePower assumes no liability for work performed, injuries, or damages on job sites', required: true },
];

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
  'VA','WA','WV','WI','WY',
];

function Register() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [userType, setUserType] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [smsEnabled, setSmsEnabled] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [dlState, setDlState] = useState('');
  const [workState, setWorkState] = useState('');
  const [agreements, setAgreements] = useState({});
  const [tosAccepted, setTosAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pendingGoogleUser, setPendingGoogleUser] = useState(null);
  const [checkingRedirect, setCheckingRedirect] = useState(true);

  useEffect(() => {
    const handleRedirect = async () => {
      try {
        const result = await getRedirectResult(auth);
        if (result?.user) {
          await handleGoogleUser(result.user);
        }
      } catch (err) {
        console.error('Redirect error:', err);
      } finally {
        setCheckingRedirect(false);
      }
    };
    handleRedirect();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleGoogleUser = async (googleUser) => {
    const userDoc = await getDoc(doc(db, 'users', googleUser.uid));
    if (userDoc.exists()) {
      navigate('/dashboard');
      return;
    }
    setPendingGoogleUser(googleUser);
    setName(googleUser.displayName || '');
    setEmail(googleUser.email || '');
    setStep(1);
  };

  const agreementsList = userType === 'worker' ? WORKER_AGREEMENTS : CONTRACTOR_AGREEMENTS;

  const allRequiredChecked = agreementsList
    .filter(a => a.required)
    .every(a => agreements[a.id]);

  const canProceedStep1 = userType !== '';

  const canProceedStep2 = pendingGoogleUser
    ? name.trim() && (userType !== 'worker' || (dlState && workState && dlState === workState))
    : name.trim() &&
      email.trim() &&
      password.length >= 6 &&
      password === confirmPassword &&
      (userType !== 'worker' || (dlState && workState && dlState === workState));

  const canSubmit = allRequiredChecked && tosAccepted;

  const toggleAgreement = (id) => {
    setAgreements(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleGoogleSignIn = async () => {
    const provider = new GoogleAuthProvider();
    setLoading(true);
    try {
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      if (isSafari || isMobile) {
        await signInWithRedirect(auth, provider);
      } else {
        const result = await signInWithPopup(auth, provider);
        await handleGoogleUser(result.user);
      }
    } catch (err) {
      setError('Google sign-in failed. Please try again.');
    }
    setLoading(false);
  };

  const saveUserData = async (uid, userName, userEmail) => {
    await setDoc(doc(db, 'users', uid), {
      name: userName,
      email: userEmail,
      userType,
      dlState: dlState || null,
      workState: workState || null,
      agreements,
      tosAccepted,
      allAgreementsComplete: true,
      phone: phone || '',
      smsEnabled: smsEnabled && !!phone.trim(),
      createdAt: new Date(),
    });

    if (userType === 'worker') {
      await setDoc(doc(db, 'workerProfiles', uid), {
        name: userName,
        email: userEmail,
        workState,
        dlState,
        phone: phone || '',
        workAuthorized: agreements['work_authorized'] || false,
        w9Consent: agreements['taxes'] || false,
        backgroundCheckConsent: false,
        agreements,
        allAgreementsComplete: true,
        createdAt: new Date(),
      });
    } else {
      await setDoc(doc(db, 'contractorProfiles', uid), {
        email: userEmail,
        userId: uid,
        phone: phone || '',
        agreements,
        insuranceConfirmed: agreements['insurance'] || false,
        allAgreementsComplete: true,
        createdAt: new Date(),
      }, { merge: true });
    }
  };

  const handleRegister = async () => {
    if (!canSubmit) return;
    setLoading(true);
    setError('');
    try {
      if (pendingGoogleUser) {
        await saveUserData(
          pendingGoogleUser.uid,
          pendingGoogleUser.displayName || name,
          pendingGoogleUser.email
        );
        navigate('/dashboard');
      } else {
        const result = await createUserWithEmailAndPassword(auth, email, password);
        await saveUserData(result.user.uid, name, email);
        navigate('/dashboard');
      }
    } catch (err) {
      if (err.code === 'auth/email-already-in-use') {
        setError('An account with this email already exists.');
      } else if (err.code === 'auth/weak-password') {
        setError('Password must be at least 6 characters.');
      } else {
        setError('Registration failed. Please try again.');
      }
    }
    setLoading(false);
  };

  if (checkingRedirect) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <h1 style={styles.logo}>TradePower</h1>
          <p style={{ color: '#666', fontSize: '14px', textAlign: 'center' }}>Setting up your account...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <h1 style={styles.logo} onClick={() => navigate('/')}>TradePower</h1>
        <p style={styles.tagline}>Find skilled workers. Find great work.</p>

        {/* Progress */}
        <div style={styles.progressRow}>
          {[
            { n: 1, label: 'Account Type' },
            { n: 2, label: 'Your Info' },
            { n: 3, label: 'Agreements' },
          ].map(s => (
            <div key={s.n} style={styles.progressStep}>
              <div style={step >= s.n ? styles.progressDotActive : styles.progressDot}>
                {step > s.n ? '✓' : s.n}
              </div>
              <span style={styles.progressLabel}>{s.label}</span>
            </div>
          ))}
        </div>

        {pendingGoogleUser && (
          <div style={styles.googleBanner}>
            🔵 Completing sign up for <strong>{pendingGoogleUser.email}</strong>
          </div>
        )}

        {error && <div style={styles.errorBox}>{error}</div>}

        {/* STEP 1 */}
        {step === 1 && (
          <div style={styles.stepContent}>
            <h2 style={styles.stepTitle}>I am a...</h2>
            <div style={styles.typeRow}>
              <div
                style={userType === 'worker' ? styles.typeCardSelected : styles.typeCard}
                onClick={() => setUserType('worker')}>
                <span style={styles.typeIcon}>👷</span>
                <p style={styles.typeTitle}>Worker</p>
                <p style={styles.typeDesc}>I'm a skilled tradesperson looking for work</p>
              </div>
              <div
                style={userType === 'contractor' ? styles.typeCardSelected : styles.typeCard}
                onClick={() => setUserType('contractor')}>
                <span style={styles.typeIcon}>🏗️</span>
                <p style={styles.typeTitle}>Contractor</p>
                <p style={styles.typeDesc}>I'm a contractor looking to hire skilled workers</p>
              </div>
            </div>
            <button
              style={canProceedStep1 ? styles.primaryBtn : styles.disabledBtn}
              disabled={!canProceedStep1}
              onClick={() => setStep(2)}>
              Continue
            </button>

            {!pendingGoogleUser && (
              <>
                <div style={styles.divider}><span>or</span></div>
                <button style={styles.googleBtn} onClick={handleGoogleSignIn} disabled={loading}>
                  {loading ? '⏳ Loading...' : '🔵 Continue with Google'}
                </button>
              </>
            )}
          </div>
        )}

        {/* STEP 2 */}
        {step === 2 && (
          <div style={styles.stepContent}>
            <h2 style={styles.stepTitle}>
              {userType === 'worker' ? 'Worker' : 'Contractor'} Account
            </h2>

            <label style={styles.fieldLabel}>Full Name *</label>
            <input style={styles.input} type="text" placeholder="Your full name"
              value={name} onChange={e => setName(e.target.value)} />

            {!pendingGoogleUser && (
              <>
                <label style={styles.fieldLabel}>Email Address *</label>
                <input style={styles.input} type="email" placeholder="you@example.com"
                  value={email} onChange={e => setEmail(e.target.value)} />

                <label style={styles.fieldLabel}>Password *</label>
                <input style={styles.input} type="password" placeholder="Minimum 6 characters"
                  value={password} onChange={e => setPassword(e.target.value)} />

                <label style={styles.fieldLabel}>Confirm Password *</label>
                <input style={styles.input} type="password" placeholder="Repeat your password"
                  value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} />
                {confirmPassword && password !== confirmPassword && (
                  <p style={styles.errorText}>Passwords do not match</p>
                )}
              </>
            )}

            <label style={styles.fieldLabel}>Phone Number (optional)</label>
            <input style={styles.input} type="tel" placeholder="(704) 555-0100"
              value={phone} onChange={e => setPhone(e.target.value)} />

            {phone.trim() && (
              <div style={styles.smsToggleBox} onClick={() => setSmsEnabled(!smsEnabled)}>
                <div style={styles.smsToggleLeft}>
                  <p style={styles.smsToggleTitle}>📱 Text Message Alerts</p>
                  <p style={styles.smsToggleDesc}>
                    Get SMS alerts for job requests, messages, and payouts
                  </p>
                </div>
                <div style={smsEnabled ? styles.smsToggleOn : styles.smsToggleOff}>
                  {smsEnabled ? 'ON' : 'OFF'}
                </div>
              </div>
            )}

            {userType === 'worker' && (
              <>
                <div style={styles.stateWarning}>
                  ⚠️ Your driver's license state must match your work state to register.
                </div>
                <div style={styles.stateRow}>
                  <div style={{ flex: 1 }}>
                    <label style={styles.fieldLabel}>Driver's License State *</label>
                    <select style={styles.input} value={dlState} onChange={e => setDlState(e.target.value)}>
                      <option value="">Select State</option>
                      {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={styles.fieldLabel}>Work State *</label>
                    <select style={styles.input} value={workState} onChange={e => setWorkState(e.target.value)}>
                      <option value="">Select State</option>
                      {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </div>
                {dlState && workState && dlState !== workState && (
                  <p style={styles.errorText}>Driver's license state and work state must match.</p>
                )}
              </>
            )}

            <div style={styles.stepButtons}>
              <button style={styles.secondaryBtn} onClick={() => setStep(1)}>Back</button>
              <button
                style={canProceedStep2 ? styles.primaryBtn : styles.disabledBtn}
                disabled={!canProceedStep2}
                onClick={() => setStep(3)}>
                Continue
              </button>
            </div>
          </div>
        )}

        {/* STEP 3 */}
        {step === 3 && (
          <div style={styles.stepContent}>
            <h2 style={styles.stepTitle}>Platform Agreements</h2>
            <p style={styles.stepSubtitle}>
              Please read and acknowledge each statement before creating your account.
            </p>

            <div style={styles.agreementsList}>
              {agreementsList.map(agreement => (
                <div
                  key={agreement.id}
                  style={agreements[agreement.id] ? styles.agreementItemChecked : styles.agreementItem}
                  onClick={() => toggleAgreement(agreement.id)}>
                  <div style={styles.agreementCheckbox}>
                    {agreements[agreement.id]
                      ? <span style={styles.checkboxChecked}>✓</span>
                      : <span style={styles.checkboxUnchecked} />}
                  </div>
                  <div style={styles.agreementTextBlock}>
                    <p style={styles.agreementText}>{agreement.text}</p>
                    {agreement.required
                      ? <span style={styles.requiredTag}>Required</span>
                      : <span style={styles.optionalTag}>Optional</span>}
                  </div>
                </div>
              ))}
            </div>

            <div
              style={tosAccepted ? styles.agreementItemChecked : styles.agreementItem}
              onClick={() => setTosAccepted(!tosAccepted)}>
              <div style={styles.agreementCheckbox}>
                {tosAccepted
                  ? <span style={styles.checkboxChecked}>✓</span>
                  : <span style={styles.checkboxUnchecked} />}
              </div>
              <div style={styles.agreementTextBlock}>
                <p style={styles.agreementText}>
                  I have read and agree to the{' '}
                  <span style={styles.tosLink} onClick={e => { e.stopPropagation(); window.open('/terms', '_blank'); }}>
                    Terms of Service
                  </span>
                  {' '}and{' '}
                  <span style={styles.tosLink} onClick={e => { e.stopPropagation(); window.open('/privacy', '_blank'); }}>
                    Privacy Policy
                  </span>
                </p>
                <span style={styles.requiredTag}>Required</span>
              </div>
            </div>

            {!allRequiredChecked && (
              <div style={styles.agreementWarning}>
                Please acknowledge all required statements to continue.
              </div>
            )}

            <div style={styles.stepButtons}>
              <button style={styles.secondaryBtn} onClick={() => setStep(2)}>Back</button>
              <button
                style={canSubmit && !loading ? styles.primaryBtn : styles.disabledBtn}
                disabled={!canSubmit || loading}
                onClick={handleRegister}>
                {loading ? 'Creating Account...' : pendingGoogleUser ? 'Complete Sign Up' : 'Create Account'}
              </button>
            </div>
          </div>
        )}

        <p style={styles.loginLink}>
          Already have an account?{' '}
          <span style={styles.link} onClick={() => navigate('/')}>Log In</span>
        </p>

        <div style={styles.footer}>
          <span style={styles.footerLink} onClick={() => navigate('/terms')}>Terms of Service</span>
          <span style={styles.footerDot}>•</span>
          <span style={styles.footerLink} onClick={() => navigate('/privacy')}>Privacy Policy</span>
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh', backgroundColor: '#f0f2f5',
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px',
  },
  card: {
    backgroundColor: 'white', borderRadius: '16px',
    boxShadow: '0 4px 24px rgba(0,0,0,0.1)', padding: '32px',
    width: '100%', maxWidth: '520px',
  },
  logo: { fontSize: '28px', fontWeight: 'bold', color: '#E94560', textAlign: 'center', marginBottom: '4px', cursor: 'pointer' },
  tagline: { textAlign: 'center', color: '#999', fontSize: '14px', marginBottom: '24px' },
  progressRow: { display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '32px', marginBottom: '28px' },
  progressStep: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' },
  progressDot: {
    width: '32px', height: '32px', borderRadius: '50%',
    backgroundColor: '#f0f0f0', color: '#999',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '13px', fontWeight: 'bold', border: '2px solid #ddd',
  },
  progressDotActive: {
    width: '32px', height: '32px', borderRadius: '50%',
    backgroundColor: '#E94560', color: 'white',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '13px', fontWeight: 'bold', border: '2px solid #E94560',
  },
  progressLabel: { fontSize: '11px', color: '#999' },
  googleBanner: {
    backgroundColor: '#e8f4fd', border: '1px solid #bee3f8',
    borderRadius: '8px', padding: '10px 14px',
    fontSize: '13px', color: '#2c5282', marginBottom: '16px', textAlign: 'center',
  },
  errorBox: {
    backgroundColor: '#f8d7da', color: '#721c24',
    padding: '10px 14px', borderRadius: '8px', fontSize: '14px', marginBottom: '16px',
  },
  stepContent: { display: 'flex', flexDirection: 'column', gap: '10px' },
  stepTitle: { fontSize: '20px', fontWeight: 'bold', color: '#1a1a2e', marginBottom: '4px' },
  stepSubtitle: { fontSize: '13px', color: '#666', marginBottom: '8px' },
  typeRow: { display: 'flex', gap: '12px', marginBottom: '8px' },
  typeCard: {
    flex: 1, padding: '20px 14px', border: '2px solid #ddd', borderRadius: '12px',
    cursor: 'pointer', textAlign: 'center',
  },
  typeCardSelected: {
    flex: 1, padding: '20px 14px', border: '2px solid #E94560', borderRadius: '12px',
    cursor: 'pointer', textAlign: 'center', backgroundColor: '#fff5f7',
  },
  typeIcon: { fontSize: '32px', display: 'block', marginBottom: '8px' },
  typeTitle: { fontSize: '15px', fontWeight: 'bold', color: '#1a1a2e', marginBottom: '4px' },
  typeDesc: { fontSize: '12px', color: '#666', lineHeight: '1.4' },
  fieldLabel: { display: 'block', fontSize: '13px', fontWeight: 'bold', color: '#555', marginBottom: '4px' },
  input: {
    width: '100%', padding: '11px 14px', borderRadius: '8px',
    border: '1px solid #ddd', fontSize: '15px',
    boxSizing: 'border-box', outline: 'none',
  },
  errorText: { fontSize: '12px', color: '#E94560', marginTop: '-6px' },
  stateWarning: {
    backgroundColor: '#fff3cd', color: '#856404',
    padding: '10px 14px', borderRadius: '8px', fontSize: '13px',
  },
  stateRow: { display: 'flex', gap: '12px' },
  smsToggleBox: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    backgroundColor: '#f8f9fa', border: '1px solid #ddd',
    borderRadius: '10px', padding: '14px', cursor: 'pointer', gap: '12px',
    touchAction: 'manipulation',
  },
  smsToggleLeft: { flex: 1 },
  smsToggleTitle: { fontSize: '14px', fontWeight: 'bold', color: '#1a1a2e', marginBottom: '2px' },
  smsToggleDesc: { fontSize: '12px', color: '#666' },
  smsToggleOn: {
    backgroundColor: '#28a745', color: 'white',
    padding: '6px 14px', borderRadius: '20px', fontSize: '12px', fontWeight: 'bold', flexShrink: 0,
  },
  smsToggleOff: {
    backgroundColor: '#ddd', color: '#666',
    padding: '6px 14px', borderRadius: '20px', fontSize: '12px', fontWeight: 'bold', flexShrink: 0,
  },
  stepButtons: { display: 'flex', gap: '10px', marginTop: '8px' },
  primaryBtn: {
    flex: 1, padding: '13px', backgroundColor: '#E94560', color: 'white',
    border: 'none', borderRadius: '8px', fontSize: '15px',
    fontWeight: 'bold', cursor: 'pointer', touchAction: 'manipulation',
  },
  secondaryBtn: {
    padding: '13px 20px', backgroundColor: 'transparent', color: '#666',
    border: '1px solid #ddd', borderRadius: '8px', fontSize: '15px', cursor: 'pointer',
  },
  disabledBtn: {
    flex: 1, padding: '13px', backgroundColor: '#ddd', color: '#999',
    border: 'none', borderRadius: '8px', fontSize: '15px',
    fontWeight: 'bold', cursor: 'not-allowed',
  },
  divider: { textAlign: 'center', color: '#ccc', fontSize: '13px', margin: '4px 0' },
  googleBtn: {
    width: '100%', padding: '13px', backgroundColor: 'white', color: '#333',
    border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px',
    fontWeight: 'bold', cursor: 'pointer', touchAction: 'manipulation',
  },
  agreementsList: { display: 'flex', flexDirection: 'column', gap: '8px' },
  agreementItem: {
    display: 'flex', gap: '12px', padding: '12px 14px',
    border: '1px solid #ddd', borderRadius: '8px', cursor: 'pointer',
    backgroundColor: 'white', alignItems: 'flex-start',
  },
  agreementItemChecked: {
    display: 'flex', gap: '12px', padding: '12px 14px',
    border: '1px solid #28a745', borderRadius: '8px', cursor: 'pointer',
    backgroundColor: '#f0fff4', alignItems: 'flex-start',
  },
  agreementCheckbox: { flexShrink: 0, paddingTop: '2px' },
  checkboxChecked: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: '20px', height: '20px', borderRadius: '4px',
    backgroundColor: '#28a745', color: 'white', fontSize: '12px', fontWeight: 'bold',
  },
  checkboxUnchecked: {
    display: 'inline-block', width: '20px', height: '20px',
    borderRadius: '4px', border: '2px solid #ddd', backgroundColor: 'white',
  },
  agreementTextBlock: { flex: 1 },
  agreementText: { fontSize: '13px', color: '#444', lineHeight: '1.5', marginBottom: '4px' },
  requiredTag: {
    display: 'inline-block', fontSize: '10px', color: '#E94560',
    backgroundColor: '#fff0f2', padding: '2px 8px', borderRadius: '4px', fontWeight: 'bold',
  },
  optionalTag: {
    display: 'inline-block', fontSize: '10px', color: '#666',
    backgroundColor: '#f0f0f0', padding: '2px 8px', borderRadius: '4px',
  },
  tosLink: { color: '#E94560', textDecoration: 'underline', cursor: 'pointer' },
  agreementWarning: {
    backgroundColor: '#fff3cd', color: '#856404',
    padding: '10px 14px', borderRadius: '8px', fontSize: '13px',
  },
  loginLink: { textAlign: 'center', fontSize: '14px', color: '#666', marginTop: '20px' },
  link: { color: '#E94560', cursor: 'pointer', fontWeight: 'bold' },
  footer: { display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', marginTop: '16px' },
  footerLink: { fontSize: '12px', color: '#999', cursor: 'pointer', textDecoration: 'underline' },
  footerDot: { fontSize: '12px', color: '#ccc' },
};

export default Register;